/**
 * Viewport renderer for the Pisa Tower simulation
 */

import type { SimulationState, Renderer } from '../types';
import { CONSTANTS } from '../simulation/physics';

export class ViewportRenderer implements Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;
  private towerImage: HTMLImageElement;
  private imageLoaded: boolean = false;

  // Coordinate conversion constants
  private readonly PADDING = 40;
  private towerLeanAngle = -5.1 * (Math.PI / 180); // 4 degrees lean

  // Tower image dimensions (original: 150x372)
  private readonly TOWER_IMAGE_ASPECT_RATIO = 150 / 372; // width/height ≈ 0.403

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    this.width = canvas.width;
    this.height = canvas.height;

    // Load tower image
    this.towerImage = new Image();
    this.towerImage.onload = () => {
      this.imageLoaded = true;
    };
    this.towerImage.src = new URL('../assets/Pisa_Tower.png', import.meta.url).href;


    // Set up high DPI rendering
    this.setupCanvas();
  }

  private setupCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    this.width = this.canvas.width;
    this.height = this.canvas.height;

    this.ctx.scale(dpr, dpr);
  }

  /**
   * Calculate the required canvas height based on tower image aspect ratio
   * This ensures the tower maintains its proper proportions
   */
  private calculateRequiredHeight(canvasWidth: number): number {
    // The tower image should fit comfortably with padding
    // We want the tower to be centered with some space on the sides
    const availableWidth = canvasWidth - 2 * this.PADDING;

    // The tower should take up about 30-40% of the available width
    // This leaves room for the balls and trails on the right
    const towerWidthPixels = availableWidth * 0.35;

    // Calculate tower height from width using aspect ratio
    const towerHeightPixels = towerWidthPixels / this.TOWER_IMAGE_ASPECT_RATIO;

    // Total height: top padding + tower + bottom padding (extra space for ground)
    const totalHeight = this.PADDING + towerHeightPixels + this.PADDING * 1.5;

    return totalHeight;
  }

  resize(): void {
    const parentElement = this.canvas.parentElement;
    if (!parentElement) return;

    // Get parent's current width
    const rect = parentElement.getBoundingClientRect();

    // Calculate required height based on content
    const calculatedHeight = this.calculateRequiredHeight(rect.width);

    // Apply sensible bounds (mobile-friendly minimum, generous maximum)
    const height = Math.max(400, Math.min(800, calculatedHeight));

    // Set height on PARENT, not canvas
    parentElement.style.height = `${height}px`;

    // Canvas fills parent via CSS
    this.setupCanvas();
  }

  /**
   * Convert simulation position (meters from top) to canvas Y coordinate
   */
  private positionToY(position: number): number {
    const availableHeight = this.height / window.devicePixelRatio - 2 * this.PADDING;
    const y = this.PADDING + (position / CONSTANTS.TOWER_HEIGHT) * availableHeight;
    return y;
  }

  /**
   * Get X coordinate for tower at given Y
   */
  private getTowerX(y: number): number {
    const centerX = 0.2 * (this.width / window.devicePixelRatio);
    const heightFromTop = y - this.PADDING;
    return centerX + heightFromTop * Math.tan(this.towerLeanAngle);
  }

  /**
   * Draw the Pisa Tower
   */
  private drawTower(): void {
    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    const topY = this.PADDING;
    const bottomY = this.positionToY(CONSTANTS.TOWER_HEIGHT);

    // Draw ground
    ctx.fillStyle = '#22c55e'; // green
    ctx.fillRect(0, bottomY, canvasWidth, canvasHeight - bottomY);

    // Draw ground line
    ctx.strokeStyle = '#166534'; // dark green
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, bottomY);
    ctx.lineTo(canvasWidth, bottomY);
    ctx.stroke();

    // Draw tower image if loaded
    if (this.imageLoaded) {
      // Calculate tower height from available vertical space
      const towerHeight = bottomY - topY;

      // Calculate tower width maintaining aspect ratio
      const towerWidth = towerHeight * this.TOWER_IMAGE_ASPECT_RATIO;

      // Calculate position for the center of the tower at the top
      const topX = this.getTowerX(topY);

      // Draw the image at the calculated position, maintaining aspect ratio
      ctx.drawImage(
        this.towerImage,
        topX - towerWidth / 2,
        topY,
        towerWidth,
        towerHeight
      );
    }
  }

  /**
   * Draw trail markers for a ball
   */
  private drawTrail(
    trail: Array<{ position: number; time: number }>,
    color: string,
    offsetX: number,
    startPosition: number
  ): void {
    const ctx = this.ctx;
    const baseX = this.getTowerX(0) + offsetX;

    // Draw vertical line for the trail
    if (trail.length > 0) {
      ctx.strokeStyle = color + '40'; // semi-transparent
      ctx.lineWidth = 2;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      // Start trail from the ball's starting position
      const startY = this.positionToY(startPosition);
      ctx.moveTo(baseX, startY);
      const lastY = this.positionToY(trail[trail.length - 1].position);
      ctx.lineTo(baseX, lastY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw markers
    ctx.fillStyle = color;
    for (const marker of trail) {
      const y = this.positionToY(marker.position);
      ctx.beginPath();
      ctx.arc(baseX, y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  /**
   * Draw a ball
   */
  private drawBall(
    position: number,
    radius: number,
    color: string,
    offsetX: number
  ): void {
    const ctx = this.ctx;
    const y = this.positionToY(position);
    const x = this.getTowerX(0) + offsetX;

    // Convert radius from meters to pixels (approximate scale)
    const availableHeight = (this.height / window.devicePixelRatio) - 2 * this.PADDING;
    const pixelsPerMeter = availableHeight / CONSTANTS.TOWER_HEIGHT;
    const radiusPixels = Math.max(5, radius * pixelsPerMeter);

    // Draw ball shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.beginPath();
    ctx.arc(x + 2, y + 2, radiusPixels, 0, 2 * Math.PI);
    ctx.fill();

    // Draw ball
    const gradient = ctx.createRadialGradient(
      x - radiusPixels / 3,
      y - radiusPixels / 3,
      0,
      x,
      y,
      radiusPixels
    );
    gradient.addColorStop(0, this.lightenColor(color));
    gradient.addColorStop(1, color);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radiusPixels, 0, 2 * Math.PI);
    ctx.fill();

    // Draw ball outline
    ctx.strokeStyle = this.darkenColor(color);
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  /**
   * Lighten a color
   */
  private lightenColor(color: string): string {
    // Simple color lightening for hex colors
    if (color === '#3b82f6') return '#93c5fd'; // blue
    if (color === '#ef4444') return '#fca5a5'; // red
    return color;
  }

  /**
   * Darken a color
   */
  private darkenColor(color: string): string {
    if (color === '#3b82f6') return '#1e40af'; // blue
    if (color === '#ef4444') return '#991b1b'; // red
    return color;
  }

  /**
   * Calculate sky color based on air density
   * 0 kg/m³ → black (space/vacuum)
   * 1.250 kg/m³ → sky blue #87ceeb (Earth atmosphere)
   * 5.0 kg/m³ → white (very dense atmosphere)
   */
  private getSkyColor(airDensity: number): string {
    const EARTH_DENSITY = 1.250;
    const MAX_DENSITY = 5.0;
    const SKY_BLUE = { r: 0x87, g: 0xce, b: 0xeb };
    const BLACK = { r: 0x00, g: 0x00, b: 0x00 };
    const WHITE = { r: 0xff, g: 0xff, b: 0xff };

    let r: number, g: number, b: number;

    if (airDensity <= EARTH_DENSITY) {
      // Interpolate from black to sky blue
      const t = airDensity / EARTH_DENSITY; // 0 to 1
      r = Math.round(BLACK.r + (SKY_BLUE.r - BLACK.r) * t);
      g = Math.round(BLACK.g + (SKY_BLUE.g - BLACK.g) * t);
      b = Math.round(BLACK.b + (SKY_BLUE.b - BLACK.b) * t);
    } else {
      // Interpolate from sky blue to white
      const t = (airDensity - EARTH_DENSITY) / (MAX_DENSITY - EARTH_DENSITY); // 0 to 1
      const clampedT = Math.min(t, 1); // clamp to 1
      r = Math.round(SKY_BLUE.r + (WHITE.r - SKY_BLUE.r) * clampedT);
      g = Math.round(SKY_BLUE.g + (WHITE.g - SKY_BLUE.g) * clampedT);
      b = Math.round(SKY_BLUE.b + (WHITE.b - SKY_BLUE.b) * clampedT);
    }

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  /**
   * Render the complete scene
   */
  render(state: SimulationState): void {
    // Update canvas resolution before rendering to prevent text stretching during resize
    this.setupCanvas();

    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    // Clear canvas with dynamic sky color based on air density
    ctx.fillStyle = this.getSkyColor(state.airDensity);
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw all elements
    this.drawTower();

    // Draw trails (offset from tower center, starting from initial ball positions)
    // Balls start at position 5 meters (see physics.ts createBall function)
    const BALL_START_POSITION = 5;
    this.drawTrail(state.blueTrail, state.blueBall.color, 100, BALL_START_POSITION);
    this.drawTrail(state.redTrail, state.redBall.color, 130, BALL_START_POSITION);

    // Draw balls
    if (!state.blueBall.hasLanded || state.blueBall.position < CONSTANTS.TOWER_HEIGHT) {
      this.drawBall(
        state.blueBall.position,
        state.blueBall.radius,
        state.blueBall.color,
        100
      );
    }

    if (!state.redBall.hasLanded || state.redBall.position < CONSTANTS.TOWER_HEIGHT) {
      this.drawBall(
        state.redBall.position,
        state.redBall.radius,
        state.redBall.color,
        130
      );
    }

    // Draw time display
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 16px Inter, sans-serif';
    ctx.fillText(`Time: ${state.time.toFixed(2)}s`, 10, 25);
  }
}
